import { useState } from 'react'

import { useQuery } from '@apollo/client'
import {
  Box,
  Button,
  Divider,
  Form,
  InfoDot,
  Link,
  Tag,
  Text,
} from '@factorialco/deprecated-design-system'
import { BudgetFragment, GetAllocationDocument } from 'generated/resources/graphql'
import moment from 'moment'

import MoneyField from 'modules/core/components/MoneyField'
import Sidepanel from 'modules/core/components/Sidepanel'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useForm } from 'modules/core/hooks/useForm'
import { fromCents, toCents } from 'modules/core/lib/formatCurrencyUtils'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import { isNumber } from 'modules/core/lib/validateNumberInput'
import app from 'modules/core/store/models/app'

import BudgetExceededOptionSelector, {
  BudgetExceededAmounts,
  BudgetExceededOptions,
} from 'modules/finance/components/NewBudgetWizard/Spenders/BudgetExceededOptionSelector'
import { useBulkUpdateBudgetAllocation } from 'modules/finance/hooks/useBudgetAllocations'
import {
  allocationStatusColor,
  allocationStatusText,
  allocationTagColor,
} from 'modules/finance/lib/budgetTagInfo'
import { amountWithCurrency, formatPeriod } from 'modules/finance/lib/budgets'

interface FormValues {
  amountCents?: string
  budgetAmountCents?: string
}
interface EmployeeDetailProps {
  visible: boolean
  allocationId: number
  budget: BudgetFragment
  periodStart?: string
  periodEnd?: string
  handleClose: () => void
  goToRecordsTab: () => void
  toggleDeleteModal: (value: boolean) => () => void
  refetchBudget: (param: { id: number; periodStart: string }) => void
}
const EmployeeDetail = ({
  visible,
  allocationId,
  budget,
  periodStart,
  periodEnd,
  handleClose,
  goToRecordsTab,
  toggleDeleteModal,
  refetchBudget,
}: EmployeeDetailProps) => {
  const {
    access: { locale },
  } = useCurrentSession()
  const currentDate = moment(budget.periodStart)
  const [budgetExceededAmounts, setBudgetExceededAmounts] = useState<BudgetExceededAmounts>()

  const { data } = useQuery(GetAllocationDocument, {
    variables: { ids: [allocationId], budgetId: budget.id, periodEnd, periodStart },
  })

  const allocation = data?.finance.budgetAllocationsConnection.nodes[0]
  const employee = allocation?.employee
  const reportedAmount = Number(allocation?.reportedAmountCents)
  const availableAmount = Number(allocation?.availableAmountCents)

  const { bulkUpdateBudgetAllocation } = useBulkUpdateBudgetAllocation({
    budgetId: budget.id,
  })

  const form = useForm<FormValues>({
    defaultValues: {
      amountCents: '',
      budgetAmountCents: '',
    },
    onSubmit: async (formData, { setErrors }) => {
      if (!allocation) return Promise.reject()

      if (formData.amountCents) {
        // Check that the amount is a positive number
        if (!isNumber(formData.amountCents) || Number(formData.amountCents) < 0) {
          setErrors({ amountCents: i18n.t('budgets.forms.errors.invalid_amount') })
          return Promise.reject()
        }
        const allocatedAmount = Number(formData.amountCents)
        const budgetAmount =
          Number(formData.budgetAmountCents) || fromCents(Number(budget.amountCents))

        // Subtract the user's allocation (if any) from the previous allocated total
        const totalAllocatedAmount =
          fromCents(Number(budget.totalAllocatedAmountCents)) -
          fromCents(Number(allocation?.amountCents))

        // Check that the new amount + previous total does not exceed the budget limit
        if (allocatedAmount + totalAllocatedAmount > budgetAmount) {
          setBudgetExceededAmounts({
            budgetAmount,
            allocatedAmount,
            totalAllocatedAmount: allocatedAmount + totalAllocatedAmount,
            maxAllocationAmount: budgetAmount - totalAllocatedAmount,
          })
          setErrors({ amountCents: i18n.t('budgets.forms.errors.allocation_exceeded') })

          return Promise.reject()
        }
      }

      const result = await bulkUpdateBudgetAllocation({
        ids: [allocation.id],
        budgetId: budget.id,
        amountCents: formData.amountCents ? toCents(Number(formData.amountCents)) : null,
        budgetLimitAmountCents: formData.budgetAmountCents
          ? toCents(Number(formData.budgetAmountCents))
          : null,
      })

      const handled = handleResult(result, () => {})

      if (handled.success) {
        refetchBudget({
          id: budget.id,
          periodStart: budget.currentPeriodStart,
        })
        handleClose()
        app.setFlash({ type: 'correct', message: i18n.t('common.saved_successfully') })
      } else {
        app.setFlash({ type: 'wrong', message: i18n.t('errors.generic_saving') })
      }
      return handled
    },
  })

  function handleBudgetExceededOption(option: BudgetExceededOptions) {
    if (!budgetExceededAmounts) return
    form.setErrors({})

    switch (option) {
      case 'increaseBudget':
        form.setValues({
          ...form.values,
          amountCents: budgetExceededAmounts.allocatedAmount.toString(),
          budgetAmountCents: budgetExceededAmounts.totalAllocatedAmount.toString(),
        })
        return
      case 'changeAllocation':
        form.setValues({
          ...form.values,
          amountCents: budgetExceededAmounts.maxAllocationAmount.toString(),
          budgetAmountCents: '',
        })
        return
    }
  }

  useEffect(() => {
    if (allocation?.amountCents) {
      form.setValues({ amountCents: fromCents(Number(allocation.amountCents)).toString() })
    }
  }, [allocation?.amountCents]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Sidepanel
      width="wide"
      open={visible}
      title={i18n.t('common.employee')}
      onClose={handleClose}
      footer={
        <Box flexDirection="rowReverse" gap="s12">
          <Button onClick={form.submitForm} disabled={form.isSubmitting}>
            {i18n.t('common.save')}
          </Button>
          <Button onClick={handleClose} type="secondary">
            {i18n.t('common.cancel')}
          </Button>
        </Box>
      }
    >
      <Form padding="s6">
        <Box gap="s12">
          <Text.Normal size="200" weight="medium" color="grey1000">
            {i18n.t('budgets.detail.tabs.employees.detail.employee')}
          </Text.Normal>
          <Text.Normal>{employee?.fullName}</Text.Normal>
        </Box>
        <Box gap="s12">
          <Text.Normal size="200" weight="medium" color="grey1000">
            {i18n.t('budgets.detail.tabs.employees.detail.legal_entity')}
          </Text.Normal>
          <Text.Normal>{employee?.legalEntity?.legalName}</Text.Normal>
        </Box>

        <Box paddingY="s12">
          <Divider />
        </Box>

        <Box gap="s24">
          <Box gap="s12">
            <Text.Normal weight="semibold" color="grey1000">
              {i18n.t('budgets.detail.tabs.employees.detail.spending')}
            </Text.Normal>
            <Text.Normal>
              {i18n.t('budgets.detail.tabs.employees.detail.spending_description')}
            </Text.Normal>
            <Link.Touchable
              onClick={() => {
                goToRecordsTab()
              }}
            >
              {i18n.t('budgets.detail.tabs.employees.detail.go_to_expending')}
            </Link.Touchable>
          </Box>
          <Box gap="s12">
            <Text.Header3>{i18n.t('budgets.detail.tabs.employees.detail.period')}</Text.Header3>
            <Text.Normal color="grey1000" whiteSpace="nowrap">
              {formatPeriod(currentDate, budget.periodicity)}
            </Text.Normal>
          </Box>
          <Box flexDirection="row">
            <Box gap="s12" width="full">
              <Text.Header3>
                {i18n.t('budgets.detail.tabs.employees.detail.employee_budget')}
              </Text.Header3>
              {allocation?.status && (
                <Box width="fit-content">
                  <Tag leftItem="circle" showCircle type={allocationTagColor(allocation?.status)}>
                    {allocationStatusText(allocation?.status)}
                  </Tag>
                </Box>
              )}
            </Box>
            <Box width="full">
              <Box width="fit-content">
                <MoneyField
                  currency={budget.currency}
                  label={i18n.t('budgets.detail.tabs.employees.detail.allocation')}
                  placeholder="0"
                  value={form.values.amountCents}
                  onChange={(value) => form.setValues({ ...form.values, amountCents: value })}
                  error={form.errors.amountCents}
                />
              </Box>
            </Box>
          </Box>
          {budgetExceededAmounts && (
            <Box gap="s16" paddingBottom="s8">
              <Box gap="s8">
                <Text.Normal weight="semibold" color="grey1000">
                  {i18n.t('budgets.detail.tabs.employees.detail.budget_exceeded_prompt')}
                </Text.Normal>
                <Text.Normal>
                  {i18n.t('budgets.detail.tabs.employees.detail.budget_exceeded_description')}
                </Text.Normal>
              </Box>
              <BudgetExceededOptionSelector
                size="small"
                currency={budget.currency}
                amounts={budgetExceededAmounts}
                onSelect={(option) => {
                  handleBudgetExceededOption(option)
                }}
              />
            </Box>
          )}
          <Box flexDirection="row">
            <Box gap="s12" width="full">
              <Text.Header3>{i18n.t('budgets.detail.tabs.employees.detail.reported')}</Text.Header3>
              <Text.Normal color="grey1000" whiteSpace="nowrap">
                {amountWithCurrency(reportedAmount, budget.currency, locale)}
              </Text.Normal>
            </Box>
            <Box gap="s12" width="full">
              <Text.Header3>
                {i18n.t('budgets.detail.tabs.employees.detail.available')}
              </Text.Header3>
              {allocation?.status && (
                <Box gap="s12" flexDirection="row" alignItems="center">
                  <>
                    <Text.Normal
                      color={
                        allocation.status === 'over_allocation'
                          ? allocationStatusColor(allocation.status)
                          : 'grey1000'
                      }
                    >
                      {amountWithCurrency(Math.abs(availableAmount), budget.currency, locale)}
                    </Text.Normal>
                  </>
                  <Box width="fit-content">
                    <InfoDot size="small" color={allocationStatusColor(allocation.status)} />
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
        {budget._policies?.update?.can && (
          <>
            <Box paddingY="s12">
              <Divider />
            </Box>
            <Box gap="s12">
              <Text.Header3>
                {i18n.t('budgets.detail.tabs.employees.detail.remove_employee')}
              </Text.Header3>
              <Text.Normal>
                {i18n.t('budgets.detail.tabs.employees.detail.remove_employee_description')}
              </Text.Normal>
              <Button type="secondary" onClick={() => toggleDeleteModal(true)()}>
                {i18n.t('common.remove')}
              </Button>
            </Box>
          </>
        )}
      </Form>
    </Sidepanel>
  )
}

export default EmployeeDetail
